import React, { useEffect, useRef, useState } from "react";

/**
 * Convert an ISO date string (YYYY-MM-DD) to a German display string
 * (DD.MM.YYYY). Returns "" for empty / invalid input.
 */
export function isoToGerman(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!m) return "";
  const yyyy = m[1];
  const mm = String(parseInt(m[2], 10)).padStart(2, "0");
  const dd = String(parseInt(m[3], 10)).padStart(2, "0");
  return `${dd}.${mm}.${yyyy}`;
}

/**
 * Format a Date object as DD.MM.YYYY. Used for display-only.
 */
export function formatGermanDate(d) {
  if (!d) return "";
  try {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}.${mm}.${d.getFullYear()}`;
  } catch {
    return "";
  }
}

/**
 * Parse a user-typed string in German-or-ISO format into a normalized ISO
 * date (YYYY-MM-DD). Returns `{ iso, error }`.
 *
 * Accepted shapes:
 *   - "TT.MM.JJJJ"             -> full date, validated
 *   - "TTMMJJJJ" (8 digits)    -> full date, validated (Schnelleingabe ohne Punkte)
 *   - "MM.JJJJ"                -> expanded to first/last of month (depending on `mode`)
 *   - "JJJJ-MM-DD" / "JJJJ-MM" -> ISO, also accepted
 *   - "" / "laufend"           -> { iso: "", error: null } (treated as open)
 *
 * Rejected:
 *   - Just a year ("2024")     -> { iso: "", error: "Bitte Monat angeben …" }
 *   - Anything else            -> { iso: "", error: "Datum nicht erkannt" }
 */
export function parseGermanDate(text, mode = "von") {
  const raw = String(text || "").trim();
  if (!raw) return { iso: "", error: null };
  const low = raw.toLowerCase();
  if (low === "laufend" || low === "offen" || low === "—" || low === "-") {
    return { iso: "", error: null };
  }

  // ISO YYYY-MM-DD
  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return validate(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10), raw);
  // ISO YYYY-MM
  m = raw.match(/^(\d{4})-(\d{1,2})$/);
  if (m) return expandMonth(parseInt(m[1], 10), parseInt(m[2], 10), mode, raw);
  // German DD.MM.YYYY
  m = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return validate(parseInt(m[3], 10), parseInt(m[2], 10), parseInt(m[1], 10), raw);
  // German DDMMYYYY (8 digits without separators — Schnelleingabe)
  m = raw.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (m) return validate(parseInt(m[3], 10), parseInt(m[2], 10), parseInt(m[1], 10), raw);
  // German MM.YYYY
  m = raw.match(/^(\d{1,2})\.(\d{4})$/);
  if (m) return expandMonth(parseInt(m[2], 10), parseInt(m[1], 10), mode, raw);
  // Year-only — explicit reject with hint
  if (/^\d{4}$/.test(raw)) {
    return {
      iso: "",
      error: "Bitte Monatsangabe ergänzen (z. B. 03.2024)",
    };
  }
  return { iso: "", error: "Datum nicht erkannt — TT.MM.JJJJ verwenden" };
}

function validate(y, mo, d, raw) {
  if (mo < 1 || mo > 12 || d < 1 || d > 31) {
    return { iso: "", error: `Ungültiges Datum „${raw}"` };
  }
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() !== mo - 1 ||
    dt.getUTCDate() !== d
  ) {
    return { iso: "", error: `Ungültiges Datum „${raw}"` };
  }
  const yyyy = String(y).padStart(4, "0");
  const mm = String(mo).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return { iso: `${yyyy}-${mm}-${dd}`, error: null };
}

function expandMonth(y, mo, mode, raw) {
  if (mo < 1 || mo > 12) {
    return { iso: "", error: `Ungültiger Monat in „${raw}"` };
  }
  const lastDay = new Date(Date.UTC(y, mo, 0)).getUTCDate(); // day-0 of next month → last day current
  const day = mode === "bis" ? lastDay : 1;
  const yyyy = String(y).padStart(4, "0");
  const mm = String(mo).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return { iso: `${yyyy}-${mm}-${dd}`, error: null };
}

/**
 * <GermanDateInput value={iso} onChange={(iso)=>...} mode="von"|"bis" />
 *
 * Self-contained controlled input with:
 *   - DD.MM.JJJJ display
 *   - Live parsing on blur / Enter
 *   - Inline error message when input is invalid
 *   - mode="bis" expands a MM.JJJJ to the LAST day of the month
 *     (mode="von" → first day)
 */
export function GermanDateInput({
  value,
  onChange,
  mode = "von",
  placeholder = "TT.MM.JJJJ",
  "data-testid": testId,
  className = "",
  required = false,
  // Iter 19.63.21 — Wenn value="" UND dieser Prop gesetzt → zeige diesen
  // Text im Input (z.B. „laufend" für Bis-Datum, das aus SV-Auszug als
  // laufend markiert war). Der Text ist editierbar; sobald User ihn überschreibt
  // wird beim Blur normal geparst (leer → ""; konkretes Datum → ISO).
  emptyDisplayText = "",
  ...rest
}) {
  const [text, setText] = useState(() => isoToGerman(value) || emptyDisplayText);
  const [error, setError] = useState("");
  const lastEmittedIso = useRef(value || "");

  // Sync external value changes (e.g. AI overwrite) into local text state.
  useEffect(() => {
    if ((value || "") !== lastEmittedIso.current) {
      setText(isoToGerman(value) || emptyDisplayText);
      lastEmittedIso.current = value || "";
      setError("");
    }
    // Edge-Case: value bleibt "" aber `emptyDisplayText` hat sich geändert
    // (z.B. von "" → "laufend" durch Backend-Response). Reflect that.
    else if ((value || "") === "" && emptyDisplayText && text !== emptyDisplayText) {
      setText(emptyDisplayText);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, emptyDisplayText]);

  const commit = (raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) {
      setError("");
      if (lastEmittedIso.current !== "") {
        lastEmittedIso.current = "";
        onChange("");
      }
      return;
    }
    const { iso, error: err } = parseGermanDate(trimmed, mode);
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setText(isoToGerman(iso));
    if (iso !== lastEmittedIso.current) {
      lastEmittedIso.current = iso;
      onChange(iso);
    }
  };

  const onBlur = (e) => commit(e.target.value);
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit(e.target.value);
    }
  };
  const onChangeText = (e) => {
    setText(e.target.value);
    if (error) setError(""); // clear error while user is editing
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={onChangeText}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        data-testid={testId}
        required={required}
        aria-invalid={error ? "true" : "false"}
        className={[
          "w-full px-2 py-1.5 text-sm border rounded-sm bg-white",
          "focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400",
          error
            ? "border-status-abgelehnt-border text-status-abgelehnt-text"
            : "border-ink-200 text-ink-900",
        ].join(" ")}
        {...rest}
      />
      {error && (
        <div
          className="mt-1 text-[10px] text-status-abgelehnt-text leading-tight"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default GermanDateInput;
